/**
 * Core OAuth popup primitive used by the login SSO flows.
 *
 * Opens the IdP authorization URL in a popup, polls until the popup closes,
 * then reads the redirect URL the backend `/oauth2/redirect` page wrote into
 * `localStorage` under the `state` key, validates the `state` round-trips, and
 * returns the authorization code. This is the same mechanism the Ember login
 * used (`ui/app/utils/oauth.ts`) and the Pipelines OAuth flow uses today
 * (`features/pipelines/oauth/oauth-provider.ts`).
 *
 * NOTE: the `/oauth2/redirect` callback page is served by the backend
 * (`customerio/services` `ui_api/ember/router.go`), not by Hydra — do not add
 * client-side handling for it.
 */
import { isEmptyString } from '@/utils/string';

const OAUTH_REQUEST_KEY = '__oauth_request';

export const NO_OAUTH_WINDOW = 'Unable to open OAuth window';
export const NO_OAUTH_RESPONSE = 'OAuth response not found';

export class OAuthError extends Error {
  provider: string;

  constructor(message: string, provider: string) {
    super(message);

    this.name = 'OAuthError';
    this.provider = provider;
  }
}

export type OAuthOpenResponse = {
  authorizationCode: string;
  redirectUri: string;
  provider: string;
};

export type OAuthPopupConfig = {
  /** Provider identifier, used for window naming and error attribution. */
  provider: string;
  /** IdP authorization endpoint. */
  baseUrl: string;
  clientId: string;
  scope: string;
  /** Static provider params (e.g. Google's `access_type`). */
  extraParams?: Record<string, string | number | boolean>;
  width?: number;
  height?: number;
};

/** The redirect URI is always served by the backend at this path. */
export function getOAuthRedirectUri(): string {
  return `${window.location.origin}/oauth2/redirect`;
}

function buildAuthorizationUrl(config: OAuthPopupConfig, state: string, redirectUri: string): string {
  const url = new URL(config.baseUrl);
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    state,
    scope: config.scope,
  });

  for (const [key, value] of Object.entries(config.extraParams ?? {})) {
    params.set(key, String(value));
  }

  // Preserve any query already present on the authorization endpoint.
  for (const [key, value] of url.searchParams.entries()) {
    params.set(key, value);
  }

  url.search = params.toString();

  return url.toString();
}

function parseResponseUrl(responseUrl: string): Record<string, string> {
  const url = new URL(responseUrl);

  return Object.fromEntries(url.searchParams.entries());
}

/**
 * Open the OAuth popup and resolve with the authorization code once the popup
 * closes. Rejects with {@link OAuthError} on every failure mode (popup blocked,
 * no stored response, state mismatch, missing code) so a resolved response
 * always has its fields populated.
 */
export async function openOAuthPopup(config: OAuthPopupConfig): Promise<OAuthOpenResponse> {
  const state = window.crypto.randomUUID();
  const redirectUri = getOAuthRedirectUri();

  localStorage.setItem(OAUTH_REQUEST_KEY, state);

  const width = config.width ?? 500;
  const height = config.height ?? 500;
  const popup = window.open(
    buildAuthorizationUrl(config, state, redirectUri),
    '_blank',
    `width=${width},height=${height},left=${screen.width / 2 - width / 2},top=${screen.height / 2 - height / 2}`,
  );

  if (popup == null) {
    throw new OAuthError(NO_OAUTH_WINDOW, config.provider);
  }

  // Poll for the closed attribute: the `beforeunload` event only fires for
  // same-origin documents, and the popup navigates to the IdP (cross-origin)
  // before landing back on our redirect page.
  while (!popup.closed) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  const responseUrl = localStorage.getItem(state);
  localStorage.removeItem(state);

  if (responseUrl == null) {
    throw new OAuthError(NO_OAUTH_RESPONSE, config.provider);
  }

  const response = parseResponseUrl(responseUrl);

  if (response.state !== state) {
    throw new OAuthError("OAuth state response didn't match. Discarding.", config.provider);
  }

  // The IdP can redirect back with an error instead of a code (e.g. the user
  // denied consent) — surface it rather than reporting a missing code.
  // https://datatracker.ietf.org/doc/html/rfc6749#section-4.1.2.1
  if (!isEmptyString(response.error) || !isEmptyString(response.error_description)) {
    const detail = !isEmptyString(response.error_description) ? response.error_description : response.error;
    throw new OAuthError(`OAuth error: ${detail}`, config.provider);
  }

  if (response.code == null) {
    throw new OAuthError('OAuth authorization code not found', config.provider);
  }

  return {
    authorizationCode: response.code,
    redirectUri,
    provider: config.provider,
  };
}
